import React, { useState, useEffect } from "react";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import loginLogo from "@/components/utils/Logo CRM.png";
import laptopBg from "@/components/utils/Logo-login.png";
import { motion, AnimatePresence } from "framer-motion";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Eye, EyeOff, Shield, Mail, Lock, AlertCircle, ArrowRight, MessageCircle, ShieldCheck, Headset, Zap } from "lucide-react";
import { AuthAnimation } from "@/components/auth/AuthAnimation";

export function CloudLogo({ src }: { src: string }) {
  return (
    <motion.div
      className="relative flex items-center justify-center w-[290px] h-[200px] xs:w-[350px] xs:h-[240px] sm:w-[420px] sm:h-[290px] md:w-[480px] md:h-[330px] lg:w-[540px] lg:h-[380px] max-h-[36vh] md:max-h-[48vh] flex-shrink-0"
      initial={{ opacity: 0, scale: 0.85, y: 15 }}
      animate={{ opacity: 1, scale: 1, y: 0 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
    >
      <motion.div
        className="absolute pointer-events-none"
        style={{
          bottom: "8%",
          width: "70%",
          height: "12%",
          background: "radial-gradient(ellipse, rgba(99,102,241,0.3) 0%, transparent 75%)",
          filter: "blur(20px)",
        }}
        animate={{ opacity: [0.5, 0.8, 0.5], scaleX: [0.9, 1.1, 0.9] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      />
      <motion.div
        className="relative w-full h-full flex items-center justify-center"
        animate={{ y: [0, -10, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      >
        <svg
          viewBox="50 40 300 210"
          xmlns="http://www.w3.org/2000/svg"
          className="absolute w-full h-full"
        >
          <defs>
            <linearGradient id="cloud-front-grad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="#1e1b4b" />
              <stop offset="50%" stopColor="#111827" />
              <stop offset="100%" stopColor="#312e81" />
            </linearGradient>
            <linearGradient id="cloud-back-grad" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="rgba(37, 99, 235, 0.25)" />
              <stop offset="100%" stopColor="rgba(124, 58, 237, 0.08)" />
            </linearGradient>
            <filter id="svg-shadow-front" x="-30%" y="-30%" width="160%" height="160%">
              <feDropShadow dx="0" dy="10" stdDeviation="10" floodColor="#2563eb" floodOpacity="0.2" />
            </filter>
            <filter id="svg-shadow-back" x="-30%" y="-30%" width="160%" height="160%">
              <feDropShadow dx="0" dy="6" stdDeviation="8" floodColor="#7c3aed" floodOpacity="0.15" />
            </filter>
          </defs>
          <path
            d="M 120,190 a 40,40 0 0,1 -15,-65 a 45,45 0 0,1 55,-35 a 65,65 0 0,1 100,-5 a 45,45 0 0,1 45,40 a 35,35 0 0,1 -10,55 a 40,40 0 0,1 -55,20 a 50,50 0 0,1 -80,-2 a 40,40 0 0,1 -40,-8 z"
            fill="url(#cloud-back-grad)"
            stroke="rgba(37, 99, 235, 0.3)"
            strokeWidth="1.5"
            transform="translate(12, 10) scale(1.01)"
            filter="url(#svg-shadow-back)"
          />
          <path
            d="M 120,190 a 40,40 0 0,1 -15,-65 a 45,45 0 0,1 55,-35 a 65,65 0 0,1 100,-5 a 45,45 0 0,1 45,40 a 35,35 0 0,1 -10,55 a 40,40 0 0,1 -55,20 a 50,50 0 0,1 -80,-2 a 40,40 0 0,1 -40,-8 z"
            fill="url(#cloud-front-grad)"
            filter="url(#svg-shadow-front)"
          />
        </svg>
        <div className="absolute inset-0 flex items-center justify-center pb-2 pl-4 z-10">
          <img src={src} alt="Kersales CRM" className="w-[53%] h-auto object-contain drop-shadow-[0_4px_12px_rgba(0,0,0,0.35)]" />
        </div>
      </motion.div>
    </motion.div>
  );
}

// Componente estilo "globito" para la información
const FeatureBubble = ({ icon: Icon, text }: { icon: any, text: string }) => (
  <motion.div 
    className="flex items-center gap-3 bg-white/10 backdrop-blur-md px-5 py-3.5 rounded-full border border-white/20 shadow-lg w-max hover:bg-white/20 transition-colors"
    whileHover={{ scale: 1.05, x: 5 }}
  >
    <div className="bg-indigo-500/80 p-1.5 rounded-full shadow-sm">
      <Icon className="w-4 h-4 text-white" />
    </div>
    <span className="text-white font-medium text-[14px] drop-shadow-md">{text}</span>
  </motion.div>
);

export function LoginForm() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showAnimation, setShowAnimation] = useState(false);
  const [animationType, setAnimationType] = useState<"loading" | "login">("loading");
  const [formErrors, setFormErrors] = useState<{ email?: string; password?: string }>({});
  const [focusedField, setFocusedField] = useState<"email" | "password" | null>(null);

  const [isSubmitting, setIsSubmitting] = useState(false);

  const { login, isAuthenticated, isLoading, error, clearError } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    clearError();
    return () => clearError();
  }, [clearError]);

  useEffect(() => {
    if (isLoading && isSubmitting) {
      setAnimationType("loading");
      setShowAnimation(true);
    } else if (error) {
      setShowAnimation(false);
      setIsSubmitting(false);
    } else if (isAuthenticated && isSubmitting) {
      setAnimationType("login");
      setShowAnimation(true);
    } else {
      setShowAnimation(false);
    }
  }, [isLoading, error, isAuthenticated, isSubmitting]);

  const handleAnimationComplete = () => {
    setShowAnimation(false);
    setIsSubmitting(false);
    navigate("/index", { replace: true });
  };

  const validateForm = () => {
    const errors: { email?: string; password?: string } = {};
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) errors.email = "El correo electrónico es requerido";
    else if (!emailRegex.test(email)) errors.email = "Correo electrónico inválido";
    if (!password) errors.password = "La contraseña es requerida";
    else if (password.length < 6) errors.password = "Mínimo 6 caracteres";
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) return;
    setIsSubmitting(true);
    try {
      await login({ email: email.trim(), password, rememberMe });
    } catch (err: any) {
      setIsSubmitting(false);
      const msg = err.response?.data?.message || err.message || "Error al iniciar sesión.";
      setFormErrors((prev) => ({ ...prev, email: msg }));
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    if (name === "email") setEmail(value);
    if (name === "password") setPassword(value);
    if (formErrors[name as "email" | "password"])
      setFormErrors((prev) => ({ ...prev, [name]: undefined }));
    if (error) clearError();
  };

  useEffect(() => {
    if (isAuthenticated && !showAnimation && !isSubmitting) {
      navigate("/index", { replace: true });
    }
  }, [isAuthenticated, navigate, showAnimation, isSubmitting]);

  if (showAnimation) {
    return <AuthAnimation isOpen={true} type={animationType} onComplete={handleAnimationComplete} />;
  }

  const hasError = !!(error || formErrors.email || formErrors.password);
  const errorMsg = error || formErrors.email || formErrors.password;
  const canSubmit = !isLoading && email && password;

  return (
    <div
      className="h-screen w-full relative flex flex-col justify-center overflow-hidden px-4 md:px-8 bg-cover bg-left bg-no-repeat"
      style={{ 
        fontFamily: "'Inter', system-ui, sans-serif",
        backgroundImage: `url(${laptopBg})`,
        backgroundColor: "#1e1b4b" 
      }}
    >
      <div className="w-full max-w-[1300px] mx-auto relative z-10 flex flex-col lg:flex-row items-center justify-end gap-8 lg:gap-12 flex-1 lg:pr-24 xl:pr-48">
        
        {/* El formulario queda solo en este lado para no estorbar el diseño de la imagen */}

        {/* LADO DERECHO: Formulario de inicio de sesión más pequeño */}
        <div className="w-full max-w-[320px] flex-shrink-0 relative z-30">
           <motion.div
            className="bg-white rounded-3xl p-6 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.5)] border border-slate-100/50 relative"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5, ease: "easeOut", delay: 0.2 }}
          >
            <div className="text-center mb-5">
              <div className="w-10 h-10 bg-indigo-50/80 rounded-2xl flex items-center justify-center mx-auto mb-3 border border-indigo-100/50 shadow-sm">
                <Lock className="w-4 h-4 text-indigo-500" />
              </div>
              <h2 className="text-xl font-extrabold text-[#1e1b4b] tracking-tight">Iniciar sesión</h2>
              <p className="text-slate-500 text-[12px] mt-1 px-2 leading-tight">Ingresa tus credenciales para administrar tu CRM</p>
            </div>

            {/* Mensaje de Error */}
            <AnimatePresence>
              {hasError && (
                <motion.div
                  className="flex items-center gap-2 px-3 py-2 rounded-xl mb-4 bg-red-50 border border-red-100 text-left"
                  initial={{ opacity: 0, y: -10, height: 0 }}
                  animate={{ opacity: 1, y: 0, height: "auto" }}
                  exit={{ opacity: 0, height: 0 }}
                >
                  <AlertCircle className="w-4 h-4 flex-shrink-0 text-red-500" />
                  <p className="text-[11px] font-medium text-red-700 leading-tight">{errorMsg}</p>
                </motion.div>
              )}
            </AnimatePresence>

            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Correo */}
              <div className="space-y-1.5">
                <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500 ml-1">
                  Correo electrónico
                </Label>
                <div
                  className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border transition-colors bg-slate-50/50 focus-within:bg-white"
                  style={{
                    borderColor: focusedField === "email" ? "#6366f1" : formErrors.email ? "#fca5a5" : "#e2e8f0",
                    boxShadow: focusedField === "email" ? "0 0 0 3px rgba(99,102,241,0.1)" : "none"
                  }}
                >
                  <Mail className={`w-4 h-4 ${focusedField === "email" ? "text-indigo-500" : "text-slate-400"}`} />
                  <input
                    name="email"
                    type="email"
                    value={email}
                    onChange={handleInputChange}
                    onFocus={() => setFocusedField("email")}
                    onBlur={() => setFocusedField(null)}
                    placeholder="ejemplo@empresa.com"
                    className="flex-1 bg-transparent outline-none text-[14px] font-medium text-slate-800 placeholder-slate-400 w-full"
                    required
                    disabled={isLoading}
                    autoComplete="email"
                  />
                </div>
              </div>

              {/* Contraseña */}
              <div className="space-y-1.5">
                <div className="flex items-center justify-between ml-1">
                  <Label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    Contraseña
                  </Label>
                  <button
                    type="button"
                    onClick={() => navigate("/forgot-password")}
                    className="text-[11px] font-semibold text-indigo-600 hover:text-indigo-800 transition-colors"
                    disabled={isLoading}
                  >
                    ¿Olvidaste tu contraseña?
                  </button>
                </div>
                <div
                  className="flex items-center gap-2.5 px-3.5 py-2.5 rounded-xl border transition-colors bg-slate-50/50 focus-within:bg-white"
                  style={{
                    borderColor: focusedField === "password" ? "#6366f1" : formErrors.password ? "#fca5a5" : "#e2e8f0",
                    boxShadow: focusedField === "password" ? "0 0 0 3px rgba(99,102,241,0.1)" : "none"
                  }}
                >
                  <Lock className={`w-4 h-4 ${focusedField === "password" ? "text-indigo-500" : "text-slate-400"}`} />
                  <input
                    name="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={handleInputChange}
                    onFocus={() => setFocusedField("password")}
                    onBlur={() => setFocusedField(null)}
                    placeholder="••••••••"
                    className="flex-1 bg-transparent outline-none text-[14px] font-medium text-slate-800 placeholder-slate-400 w-full"
                    required
                    disabled={isLoading}
                    autoComplete="current-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    disabled={isLoading}
                    className="text-slate-400 hover:text-indigo-600 transition-colors"
                  >
                    {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>

              {/* Recordarme */}
              <div className="flex items-center gap-2 ml-1">
                <Checkbox
                  id="rememberMe"
                  checked={rememberMe}
                  onCheckedChange={(checked) => setRememberMe(checked as boolean)}
                  disabled={isLoading}
                  className="border-slate-300 rounded-[4px] data-[state=checked]:bg-indigo-600 data-[state=checked]:border-indigo-600 w-3.5 h-3.5"
                />
                <Label
                  htmlFor="rememberMe"
                  className="text-[12px] font-medium text-slate-600 cursor-pointer select-none"
                >
                  Recordarme
                </Label>
              </div>

              {/* Botón Iniciar sesión */}
              <button
                type="submit"
                disabled={!canSubmit}
                className="group w-full py-2.5 mt-1 rounded-xl text-[14px] font-bold text-white relative overflow-hidden flex items-center justify-center gap-2 transition-all"
                style={{
                  background: canSubmit ? "linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%)" : "#cbd5e1",
                  boxShadow: canSubmit ? "0 8px 20px -6px rgba(99,102,241,0.5)" : "none",
                  cursor: canSubmit ? "pointer" : "not-allowed",
                  transform: canSubmit ? "translateY(0)" : "none"
                }}
              >
                {isLoading ? (
                  <>
                    <svg className="w-5 h-5 animate-spin" viewBox="0 0 24 24" fill="none">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="white" strokeWidth="4" />
                      <path className="opacity-75" fill="white" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
                    </svg>
                    <span>Iniciando sesión...</span>
                  </>
                ) : (
                  <>
                    <span>Iniciar sesión</span>
                    <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
                  </>
                )}
              </button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-[12px] font-medium text-slate-500">
                ¿No tienes una cuenta?{" "}
                <button
                  type="button"
                  onClick={() => navigate("/register")}
                  className="font-bold text-indigo-600 hover:text-indigo-800 transition-colors"
                >
                  Registrar
                </button>
              </p>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Footer minimalista con posicionamiento absoluto para no generar scroll */}
      <div className="absolute bottom-6 w-full flex justify-center lg:justify-end lg:pr-24 xl:pr-48 text-[11px] font-semibold text-slate-500 drop-shadow-sm z-10">
        <p>© 2026 Kersales CRM. Todos los derechos reservados.</p>
      </div>
    </div>
  );
}
